import type { TopicContentSource } from "@shared/topic-content-sources";
import type { TopicGuide } from "@shared/topic-guides";

type LegacySource = {
  sourceId: string;
  title: string;
  url: string;
  publisher: string;
  trust: 4 | 5;
  jurisdiction: string;
  checkedAt: string;
  verificationStatus: string;
};

type LegacyGuide = {
  topicId: string;
  title: string;
  risk: TopicGuide["risk"];
  publicationStatus: TopicGuide["publicationStatus"];
  jurisdiction: string;
  checkedAt: string;
  summary: string;
  keyPoints: Array<{ text: string; sourceIds: string[] }>;
  steps: Array<{ title: string; description: string; sourceIds: string[] }>;
  checklist: { title: string; items: string[]; sourceIds: string[] };
  faqs: Array<{ question: string; answer: string; sourceIds: string[] }>;
  sources: LegacySource[];
  professionalReview: string;
  disclaimer: string;
};

type LegacyGuideModule = { guides: LegacyGuide[] };

export type LegacyTopicContent = {
  guide: TopicGuide;
  sources: TopicContentSource[];
};

const LEGACY_GUIDE_MODULE_PATHS = [
  "/topic-guides-phase-a.js",
  "/topic-guides-phase-b.js",
  "/topic-guides-phase-c.js",
] as const;

let legacyGuidePromise: Promise<Map<string, LegacyGuide>> | null = null;

function loadLegacyGuides(): Promise<Map<string, LegacyGuide>> {
  legacyGuidePromise ??= Promise.all(
    LEGACY_GUIDE_MODULE_PATHS.map(
      (path) => import(/* @vite-ignore */ path) as Promise<LegacyGuideModule>,
    ),
  ).then(
    (modules) =>
      new Map(
        modules.flatMap((module) => module.guides).map((guide) => [guide.topicId, guide]),
      ),
  );
  return legacyGuidePromise;
}

function toReviewStatus(
  status: string,
): TopicContentSource["reviewStatus"] {
  if (status === "official_document_verified") return "official_document_verified";
  if (status === "official_index_verified" || status === "official_url_confirmed") {
    return "official_index_verified";
  }
  if (
    status === "official_access_restricted" ||
    status === "official_service_javascript_limited"
  ) {
    return "official_access_limited";
  }
  return "live_verified";
}

function toJurisdiction(value: string): TopicContentSource["jurisdiction"] {
  return value.toLowerCase().includes("nsw") ? "nsw" : "australia";
}

export function adaptLegacyTopicGuide(legacy: LegacyGuide): LegacyTopicContent {
  const sourceIds = legacy.sources.map((source) => source.sourceId);
  const sources: TopicContentSource[] = legacy.sources.map((source) => ({
    sourceId: source.sourceId,
    topicId: legacy.topicId,
    title: source.title,
    publisher: source.publisher,
    url: source.url,
    trust: source.trust,
    jurisdiction: toJurisdiction(source.jurisdiction),
    checkedAt: source.checkedAt,
    verificationMethod: "기존 OZK 공식 자료 검토 기록을 SPA 가이드에 연결",
    reviewStatus: toReviewStatus(source.verificationStatus),
    claimScope: `${legacy.title}의 일반 안내와 공식 확인 경로`,
    followUp: "중요한 행동 직전에 연결된 공식 페이지의 현재 내용을 다시 확인",
  }));

  const guide: TopicGuide = {
    topicId: legacy.topicId,
    locale: "ko",
    publicationStatus: legacy.publicationStatus,
    risk: legacy.risk,
    checkedAt: legacy.checkedAt,
    jurisdictionLabel: legacy.jurisdiction,
    summary: legacy.summary,
    keyPoints: legacy.keyPoints.map((point) => point.text),
    sections: legacy.steps.map((step, index) => ({
      id: `legacy-step-${index + 1}`,
      title: `${index + 1}. ${step.title}`,
      body: [step.description],
      ...(index === 0 ? { checklist: legacy.checklist.items } : {}),
      sourceIds: step.sourceIds,
    })),
    faqs: legacy.faqs.map((faq, index) => ({
      id: `legacy-faq-${index + 1}`,
      question: faq.question,
      answer: faq.answer,
      sourceIds: faq.sourceIds,
    })),
    sourceIds,
    additionalChecks: [legacy.professionalReview],
    disclaimer: legacy.disclaimer,
  };

  return { guide, sources };
}

export async function loadLegacyTopicContent(
  topicId: string,
): Promise<LegacyTopicContent | undefined> {
  const legacy = (await loadLegacyGuides()).get(topicId);
  return legacy ? adaptLegacyTopicGuide(legacy) : undefined;
}
